import { /*Map,*/ fromJS } from "immutable";
import { handleActions, createAction } from "redux-actions";
import Restful from "lib/restful";
import { isSameObject } from "lib/commonUtil";
import { sendError } from "lib/sentryHelper";

const EXP_MINUTE = 30;

const SET_USERNAME = "myAccount/SET_USERNAME";
const SET_EMAIL = "myAccount/SET_EMAIL";
const CONFIRM_LOG_IN = "myAccount/CONFIRM_LOG_IN";
const SET_NEED_LOG_OUT = "myAccount/SET_NEED_LOG_OUT";
const SET_MEMBERSHIP_INFO = "myAccount/SET_MEMBERSHIP_INFO";
const SET_EXP_DATE_OF_MEM_INFO = "myAccount/SET_EXP_DATE_OF_MEM_INFO";
const INIT_ALL_DATA = "myAccount/INIT_ALL_DATA";

const setUsername = createAction(SET_USERNAME);
const setEmail = createAction(SET_EMAIL);
const confirmLogIn = createAction(CONFIRM_LOG_IN);
const setNeedLogOut = createAction(SET_NEED_LOG_OUT);
const setMembershipInfo = createAction(SET_MEMBERSHIP_INFO);
const setExpDateOfMemInfo = createAction(SET_EXP_DATE_OF_MEM_INFO);
const initAllData = createAction(INIT_ALL_DATA);

const getMyAccountState = ({ getState }) => {
  const myAccountState = getState().myAccount.toJS();
  return myAccountState;
};

const setExpDate = ({ dispatch }) => {
  const expirationDate = new Date();
  expirationDate.setMinutes(expirationDate.getMinutes() + EXP_MINUTE);
  dispatch(setExpDateOfMemInfo(expirationDate));
};

const writeNewMemInfo = ({ dispatch, info }) => {
  dispatch(setMembershipInfo(info));
  setExpDate({ dispatch });
};

const requestMembershipInfo = async ({ dispatch, idToken }) => {
  const { code, res, errInfo } = await Restful.getMyMembershipInfoAPI({
    idToken
  });
  if (code === "error") {
    const errObj = Error("api error");
    errObj.errorValue = { ...errInfo };
    throw errObj;
  }
  writeNewMemInfo({ dispatch, info: res });
};

const syncMembershipData = async ({ dispatch, idToken }) => {
  await requestMembershipInfo({ dispatch, idToken });
};

const writeMembershipData = async ({ dispatch, getState, type, idToken }) => {
  const { membershipInfo, expirationDate } = getMyAccountState({ getState });
  let newState, newMemInfo;
  switch (type) {
    case "force":
      await syncMembershipData({ dispatch, idToken });

      newState = getMyAccountState({ getState });
      newMemInfo = newState.membershipInfo;
      return {
        isChanged: !isSameObject(membershipInfo, newMemInfo),
        newMembershipInfo: newMemInfo
      };
    case "normal":
    default:
      if (!membershipInfo || expirationDate < new Date()) {
        await syncMembershipData({ dispatch, idToken });
      }

      newState = getMyAccountState({ getState });
      newMemInfo = newState.membershipInfo;
      return {
        isChanged: !isSameObject(membershipInfo, newMemInfo),
        newMembershipInfo: newMemInfo
      };
  }
};

export const setLogIn =
  ({ email, username, idToken }) =>
  async (dispatch, getState) => {
    dispatch(setUsername(username));
    dispatch(setEmail(email));
    dispatch(confirmLogIn());
    try {
      await writeMembershipData({
        dispatch,
        getState,
        type: "normal",
        idToken
      });
    } catch (error) {
      sendError(error);
    }
  };

export const editUsername = username => (dispatch, getState) => {
  dispatch(setUsername(username));
};

export const requestNormalLogOut = () => (dispatch, getState) => {
  dispatch(setNeedLogOut("normalLogOut"));
};

export const requestAllDeviceLogOut = () => (dispatch, getState) => {
  dispatch(setNeedLogOut("allDeviceLogOut"));
};

export const updateMembershipData = idToken => async (dispatch, getState) => {
  await writeMembershipData({ dispatch, getState, type: "normal", idToken });
};

export const forceUpdateMembershipData =
  idToken => async (dispatch, getState) => {
    const { isChanged, newMembershipInfo } = await writeMembershipData({
      dispatch,
      getState,
      type: "force",
      idToken
    });
    return { isChanged, newMembershipInfo };
  };

export const setLogOut = () => (dispatch, getState) => {
  dispatch(initAllData());
};

const initialState = fromJS({
  username: null,
  email: null,
  needLogOut: "no", // "no", "normalLogOut", "allDeviceLogOut"
  hasLoggedIn: false,

  expDateOfMemInfo: null,
  membershipInfo: null
  /*
    membershipInfo 의 구조
    membershipInfo : {
      hasMembership: true, // true,false
      userType: "basic", // "personal","basic"
      canUseFreeTrial: true, // true,undefined
      paymentType: "paypal", // "freeTrial","paypal","portone"
      isPaidUser: true, // true,false
      paidByCoupon: true, // true,false
      status: "paused", // "activated","deactivated","paused"
      startTime: "2021-06-13T12:54:50.424Z",
      endTime: "2021-06-13T12:54:50.424Z",
      nextBillingTime: "2021-06-13T12:54:50.424Z", 
      endTimeOnCancel: "2021-06-13T12:54:50.424Z",
      availableDateOfSubscription: "2021-06-13T12:54:50.424Z",
      numOfAllCoupon: 0,
      numOfPromotionCoupon: 0,
      numOfReferralCoupon: 0,
      numOfCouponUnused: 0,
      numOfCouponUsed: 0,
      invitedUserCount: 2
    }
  */
});

export default handleActions(
  {
    [SET_USERNAME]: (state, action) => {
      const username = action.payload;
      return state.set("username", fromJS(username));
    },
    [SET_EMAIL]: (state, action) => {
      const email = action.payload;
      return state.set("email", fromJS(email));
    },
    [CONFIRM_LOG_IN]: (state, action) => {
      return state.set("hasLoggedIn", fromJS(true));
    },
    [SET_NEED_LOG_OUT]: (state, action) => {
      const needLogOut = action.payload;
      return state.set("needLogOut", fromJS(needLogOut));
    },
    [SET_MEMBERSHIP_INFO]: (state, action) => {
      const membershipInfo = action.payload;
      return state.set("membershipInfo", fromJS(membershipInfo));
    },
    [SET_EXP_DATE_OF_MEM_INFO]: (state, action) => {
      const expDateOfMemInfo = action.payload;
      return state.set("expDateOfMemInfo", fromJS(expDateOfMemInfo));
    },
    [INIT_ALL_DATA]: (state, action) => {
      return initialState;
    }
  },
  initialState
);
